import { useEffect } from 'react';
import { useTranslation } from '@frontend/i18n';
import { PANEL_MIN_WIDTH } from '@frontend/design-system';
import { useNotificationPreferences } from './store';

const MS_48_HOURS = 48 * 60 * 60 * 1000;
const NOTIFICATION_TYPE = 'sms-message-new';
const PREFERENCE_ID = 'sms-reminder-after-48h';
const STORAGE_KEY = 'sms-inbox-entry-time';

export const useAutoPauseSmsWithReminder = () => {
  const { t } = useTranslation('notifications');
  const { pauseNotificationType, addSuppressRule, addPreferenceRule, isNotificationPaused, hasPreferenceDismissed } =
    useNotificationPreferences([
      'pauseNotificationType',
      'addSuppressRule',
      'addPreferenceRule',
      'isNotificationPaused',
      'hasPreferenceDismissed',
    ]);

  useEffect(() => {
    const unsubscribeSuppressRule = addSuppressRule({
      id: PREFERENCE_ID,
      for: NOTIFICATION_TYPE,
      condition: (_notification, context) => {
        const isAtInbox = context.pathname?.startsWith('/messages/inbox');
        // Exit early if not in inbox
        if (!isAtInbox) return false;

        // If already dismissed, just return pause status
        if (hasPreferenceDismissed(PREFERENCE_ID)) {
          return isNotificationPaused(NOTIFICATION_TYPE);
        }

        const multiPanelLayout = document.querySelector('#multi-panel-layout');

        const multiPanelCollapsedWidth = PANEL_MIN_WIDTH * 2;

        // Exit early if screen smaller than multiPanelCollapsedWidth i.e. 800px
        if (multiPanelLayout && multiPanelLayout?.clientWidth < multiPanelCollapsedWidth) {
          return false;
        }

        // Auto pause notifications
        pauseNotificationType(NOTIFICATION_TYPE, true);

        const now = Date.now();
        const stored = localStorage.getItem(STORAGE_KEY);

        // First-time: store timestamp and suppress notification
        if (!stored) {
          localStorage.setItem(STORAGE_KEY, String(now));
          return true;
        }

        const timeElapsed = now - Number(stored);

        // If 48 hours passed, show the preference rule again
        if (timeElapsed > MS_48_HOURS) {
          addPreferenceRule({
            id: PREFERENCE_ID,
            for: NOTIFICATION_TYPE,
            type: 'auto-pause-notifications-reminder',
            title: t('Pause SMS Notifications?'),
            body: t('Hi! We noticed you`re in the Inbox, so we paused SMS pop-ups. Keep them paused?'),
            condition: (_notification, context) => {
              return context.pathname.startsWith('/messages/inbox');
            },
          });
          return false;
        }

        return true;
      },
    });

    return () => {
      unsubscribeSuppressRule();
    };
  }, [addSuppressRule, addPreferenceRule, isNotificationPaused, t]);
};
